"use client";

import { useRef, useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Category } from "@/types";
import { mediaUrl } from "@/lib/utils";

export function CategorySlider({ categories }: { categories: Category[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const overflowing = el.scrollWidth > el.clientWidth + 1;
      setIsOverflowing(overflowing);
      setCanScrollLeft(el.scrollLeft > 4);
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [categories]);

  const scroll = (dir: "left" | "right") => {
    scrollRef.current?.scrollBy({ left: dir === "left" ? -300 : 300, behavior: "smooth" });
  };

  return (
    <div className="relative">
      {isOverflowing && canScrollLeft && (
        <button
          onClick={() => scroll("left")}
          aria-label="Sola kaydır"
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-8 h-8 bg-white rounded-full shadow-md border border-gray-100 items-center justify-center hover:bg-gray-50 transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-gray-600" />
        </button>
      )}

      <div
        ref={scrollRef}
        className={`flex gap-3 md:gap-8 overflow-x-auto px-1 md:px-0 py-3 transition-all ${isOverflowing ? "justify-start" : "justify-center"}`}
        style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none", msOverflowStyle: "none" } as React.CSSProperties}
      >
        {categories.map((cat) => (
          <Link
            key={cat.id}
            href={`/kategori/${cat.slug}`}
            className="group flex flex-col items-center text-center shrink-0"
            style={{ scrollSnapAlign: "start", width: 112 }}
          >
            <div className="relative mb-3 flex h-24 w-28 items-center justify-center overflow-hidden rounded-2xl ring-1 ring-gray-200 bg-gray-50 transition-all duration-200 group-hover:-translate-y-0.5 group-hover:ring-gray-900 group-hover:shadow-md">
              {cat.imageUrl ? (
                <Image
                  src={mediaUrl(cat.imageUrl)}
                  alt={cat.name}
                  fill
                  className="object-cover"
                  style={{ objectPosition: `${cat.focalX ?? 50}% ${cat.focalY ?? 50}%` }}
                  sizes="112px"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-gray-50">
                  <svg className="h-8 w-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
              )}
            </div>
            <p className="w-full px-1 text-sm font-semibold leading-snug text-gray-900 transition-colors group-hover:text-black md:text-[15px] line-clamp-2">
              {cat.name}
            </p>
            <p className="mt-1 w-full px-1 text-[11px] leading-4 text-gray-500 line-clamp-2">
              {cat.description ?? (cat.productCount != null ? `${cat.productCount} ürün` : "")}
            </p>
          </Link>
        ))}
      </div>

      {isOverflowing && canScrollRight && (
        <button
          onClick={() => scroll("right")}
          aria-label="Sağa kaydır"
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-8 h-8 bg-white rounded-full shadow-md border border-gray-100 items-center justify-center hover:bg-gray-50 transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-gray-600" />
        </button>
      )}
    </div>
  );
}
